import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import downloadFile from "_shared/_utilities/download-file";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import Guid from "_shared/guids/guid";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import {
  useCreateDocumentsMutation,
  useGetDocumentByIdQuery,
  useLazyDownloadDocumentQuery,
} from "documents/_api/document-api";
import { documentDownloadFailureMessage } from "documents/_api/document-download-failure";
import {
  getDocumentUploadSnackbar,
  summarizeDocumentUploadResults,
} from "documents/_api/document-upload-results";
import Document from "documents/document";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import DocumentDownloadControl from "documents/downloads/_views/document-download-control";
import DocumentTopic from "documents/topics/document-topic";
import {
  useAttachKnowledgeBaseDocumentsMutation,
  useDetachKnowledgeBaseDocumentMutation,
} from "knowledge-base/_api/knowledge-base-api";
import KnowledgeBaseArticle from "knowledge-base/knowledge-base-article";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useRef, useState } from "react";

type Props = Readonly<{
  article: KnowledgeBaseArticle;
  /**
   * Whether this reader may change what is attached. An affordance only — the server refuses an
   * attach this person may not make whatever the browser draws.
   */
  canEdit?: boolean;
}>;

type RowProps = Readonly<{
  documentId: string;
  canEdit: boolean;
  onDetach: (documentId: string, name: string) => void;
}>;

const DropTarget = styled(Box, {
  shouldForwardProp: (property) => property !== "isDragging",
})<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  alignItems: "center",
  backgroundColor: isDragging ? theme.palette.action.hover : "transparent",
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  color: isDragging ? theme.palette.primary.main : theme.palette.text.secondary,
  cursor: "copy",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  justifyContent: "center",
  padding: theme.spacing(3),
  textAlign: "center",
}));

/**
 * One attachment, asked for on its own.
 *
 * Per document rather than one batch call for all of them, because the batch route refuses the
 * whole list when the caller may not read any one of its members — "refuse rather than quietly
 * shorten the list", which is the right answer for a caller that named the ids and the wrong shape
 * for a list drawn from an article. Asked one at a time, a document the reader may not open
 * becomes a locked row beside the ones they can, instead of emptying the section.
 *
 * That row matters: a reader who can see a procedure but not the form it refers to needs to know
 * the form exists and that access is the missing piece, rather than seeing a shorter list than
 * their colleague and never knowing it.
 */
function AttachmentRow(props: RowProps) {
  const documentId = props.documentId;
  const { data: attachment, isLoading, isError } = useGetDocumentByIdQuery({ documentId });
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  const isReadable = attachment instanceof Document;
  const name = isReadable ? attachment.name.value : "Restricted file";

  const download = async () => {
    try {
      const response = await downloadDocument({ documentId }).unwrap();
      await downloadFile(response);
    } catch (error) {
      enqueueSnackbar(documentDownloadFailureMessage(error, `Failed to download ${name}`), { variant: "error" });
    }
  };

  if (isLoading) {
    return (
      <ListItem sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <CircularProgress size={16} />
        </ListItemIcon>
        <ListItemText primary="Loading…" />
      </ListItem>
    );
  }

  return (
    <ListItem
      secondaryAction={
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {isReadable && (
            <DocumentDownloadControl
              document={attachment}
              name={name}
              onDownload={() => void download()}
              edge="end"
              size="small"
              iconFontSize="small"
            />
          )}
          {/*
            Offered on a locked row too. Detaching is an act on the article, not on the file, and
            the server asks only whether this person may author here — so an editor can clear a
            reference to something a colleague attached and they cannot read.
          */}
          {props.canEdit && (
            <Tooltip title={`Remove ${name}`}>
              <DeleteIconButton
                aria-label={`Remove ${name}`}
                edge="end"
                onClick={() => props.onDetach(documentId, name)}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </DeleteIconButton>
            </Tooltip>
          )}
        </Box>
      }
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        {isReadable ? <AttachFileIcon fontSize="small" /> : <LockOutlinedIcon fontSize="small" />}
      </ListItemIcon>
      <ListItemText
        primary={name}
        secondary={
          isError || !isReadable
            ? "You do not have access to this document. A manager may be able to grant it."
            : <DocumentProcessingState status={attachment.classificationStatus} scanStatus={attachment.scanStatus} />
        }
        slotProps={{ secondary: { component: "span" } }}
      />
    </ListItem>
  );
}

/**
 * The documents an article refers to, and the way new ones get there.
 *
 * Uploading is two steps on purpose. The file goes to the Documents center first, which owns
 * storage, mime-type validation, size limits and access control; only then is the resulting id
 * attached here. A second file pipeline living in the knowledge base would be a mistake, and it is
 * also what keeps one set of rules deciding who may open a file.
 */
export default function ArticleAttachments(props: Props) {
  // Read rather than destructured with a default: a default inside a destructuring pattern is one
  // of the forms the React Compiler cannot lower, and it bails out of the whole component
  // silently. See app/CLAUDE.md.
  const article = props.article;
  const canEdit = props.canEdit ?? false;

  const filePicker = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const documentIds = article.attachedDocumentIds;

  const [createDocuments, { isLoading: isUploading }] = useCreateDocumentsMutation();
  const [attachDocuments, { isLoading: isAttaching }] = useAttachKnowledgeBaseDocumentsMutation();
  const [detachDocument] = useDetachKnowledgeBaseDocumentMutation();

  const isBusy = isUploading || isAttaching;

  const upload = async (files: File[]) => {
    if (files.length === 0) return;
    setFailure(undefined);

    // Topic-tagged with the article, so a document's own screens can say what refers to it rather
    // than showing a file that arrived from nowhere.
    const requests = createBulkDocumentRequests(files, {
      topics: [
        DocumentTopic.create({
          entityClass: "KnowledgeBaseArticle",
          entityId: new Guid(article.id),
        }),
      ],
    });

    const results = await createDocuments({ requests });
    if ("error" in results) {
      setFailure("The files could not be uploaded.");
      return;
    }

    // The bulk upload answers per file, so a rejected one is named rather than sinking the batch.
    const summary = summarizeDocumentUploadResults(results.data);
    const snackbar = getDocumentUploadSnackbar(summary);
    enqueueSnackbar(snackbar.message, { variant: snackbar.variant });

    // Attach whatever did upload. Refusing to attach five good files because a sixth was the wrong
    // type would make the author repeat work the server already did.
    if (summary.documents.length === 0) return;

    const attached = await attachDocuments({
      articleId: article.id,
      documentIds: summary.documents.map((document) => document.id.value),
    });

    if ("error" in attached) {
      setFailure(
        getErrorMessageFromUnknown(attached.error) ??
          "The files were uploaded but could not be attached to this page.",
      );
    }
  };

  const detach = async (documentId: string, name: string) => {
    setFailure(undefined);
    const result = await detachDocument({ articleId: article.id, documentId });
    if ("error" in result) {
      setFailure(getErrorMessageFromUnknown(result.error) ?? `${name} could not be removed.`);
    }
  };

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    await upload(Array.from(event.dataTransfer.files));
  };

  return (
    <Box component="section" data-testid="article-attachments">
      <Typography component="h2" sx={{ mb: 1 }} variant="subtitle1">
        Attachments
      </Typography>

      {failure && (
        <Alert onClose={() => setFailure(undefined)} severity="error" sx={{ mb: 2 }}>
          {failure}
        </Alert>
      )}

      {documentIds.length === 0 && !canEdit && (
        <Typography color="text.secondary" variant="body2">
          Nothing is attached to this page.
        </Typography>
      )}

      {documentIds.length > 0 && (
        <List dense disablePadding>
          {documentIds.map((documentId) => (
            <AttachmentRow
              canEdit={canEdit}
              documentId={documentId}
              key={documentId}
              onDetach={detach}
            />
          ))}
        </List>
      )}

      {canEdit && (
        <>
          <DropTarget
            isDragging={isDragging}
            onDragLeave={() => setIsDragging(false)}
            onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDrop={onDrop}
          >
            {isBusy ? <CircularProgress size={24} /> : <UploadFileIcon fontSize="large" />}
            <Typography variant="body2">
              {isBusy ? "Uploading…" : "Drop files here to attach them to this page"}
            </Typography>
            <Button
              disabled={isBusy}
              onClick={() => filePicker.current?.click()}
              size="small"
              variant="outlined"
            >
              Choose files
            </Button>
          </DropTarget>

          <input
            aria-label="Attach files to this page"
            hidden
            multiple
            onChange={async (event) => {
              await upload(Array.from(event.target.files ?? []));
              // Cleared so choosing the same file twice in a row still raises a change event.
              event.target.value = "";
            }}
            ref={filePicker}
            type="file"
          />
        </>
      )}
    </Box>
  );
}
